import queryString from "query-string";
import React from "react";
import type { ApolloClient } from "@apollo/client";
import { compact, get, has, keyBy, map, transform } from "lodash";
import { Col, Form, Row } from "react-bootstrap";
import Button from "components/Button";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiCalendar } from "react-icons/fi";
import Alert from "components/Alert";

import { ScrollToFieldError } from "utils/form";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { createCustomReport, getCustomReportTypes } from "actions/adminActions";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import IconSpinner from "components/IconSpinner";
import { push } from "store/actions/router";
import { toast } from "react-toastify";
import type { RootState } from "types/state";

import * as yup from "yup";

/** A report parameter as described by the getReportTypes query selection. */
interface ReportParameter {
  name: string;
  description: string;
  type: string;
  isArray: boolean;
  required: boolean;
}

/** A custom report type, keyed by `reportType` in customReportTypes. */
interface CustomReportType {
  description: string;
  reportType: string;
  parameters: ReportParameter[];
}

/**
 * Formik values: a dynamic bag keyed by parameter name plus reportName. Date
 * fields hold Date objects, Str fields hold strings; values may be undefined
 * before a field is touched (initialValues uses `get(..., undefined)`).
 */
type FormValues = Record<string, unknown> & { reportName: string };

const parseDate = (val: unknown) => {
  if (val) {
    return Date.parse(val as string);
  }
  return val;
};

function generateDateField({
  handleBlur,
  values,
  touched,
  errors,
  setFieldValue,
  inputParams,
}: Pick<
  FormikProps<FormValues>,
  "handleBlur" | "values" | "touched" | "errors" | "setFieldValue"
> & { inputParams: ReportParameter }) {
  return (
    <>
      <Form.Label>
        {inputParams.description} {inputParams.required ? "(Required)" : ""}
      </Form.Label>

      <div className="date-picker">
        <span className="calendar">
          <FiCalendar
            size={"18px"}
            color={"#FFFFFF"}
            fill={"#FFFFFF"}
            stroke={"#AAB1BF"}
            strokeWidth={"2"}
          />
        </span>
        <DatePicker
          className="form-control"
          name={inputParams.name}
          // @ts-expect-error react-datepicker has no `value` prop (it derives
          // the displayed text from `selected`); inert, preserved from master.
          value={parseDate(values[inputParams.name])}
          dateFormat="MM/dd/yyyy"
          selected={
            // values[name] is unknown (dynamic bag); the original computes
            // `(value && new Date(value)) || null` — coerce the result to the
            // prop's type, runtime expression unchanged.
            ((values[inputParams.name] &&
              new Date(values[inputParams.name] as string)) ||
              null) as Date | null
          }
          onChange={(val: Date | null) => {
            void setFieldValue(inputParams.name, new Date(val as Date));
          }}
          onBlur={handleBlur}
          isInvalid={touched[inputParams.name] && !!errors[inputParams.name]}
          isValid={touched[inputParams.name] && !errors[inputParams.name]}
        />
      </div>
      {errors[inputParams.name] && touched[inputParams.name] && (
        <div className="invalid-feedback" style={{ display: "block" }}>
          {errors[inputParams.name] as React.ReactNode}
        </div>
      )}
    </>
  );
}

function generateStringInputField({
  handleChange,
  handleBlur,
  values,
  touched,
  errors,
  inputParams,
}: Pick<
  FormikProps<FormValues>,
  "handleChange" | "handleBlur" | "values" | "touched" | "errors"
> & { inputParams: ReportParameter }) {
  return (
    <>
      <Form.Label>
        {inputParams.description} {inputParams.required ? "(Required)" : ""}
      </Form.Label>
      <Form.Control
        name={inputParams.name}
        as={inputParams.isArray ? "textarea" : "input"}
        value={values[inputParams.name] as string}
        onChange={handleChange}
        onBlur={handleBlur}
        isInvalid={touched[inputParams.name] && !!errors[inputParams.name]}
        isValid={touched[inputParams.name] && !errors[inputParams.name]}
      />
      <Form.Control.Feedback type="invalid">
        {errors[inputParams.name] as React.ReactNode}
      </Form.Control.Feedback>
    </>
  );
}

function cleanAndSplitIds(idsString: string) {
  if (idsString.includes(",")) {
    idsString = idsString.replace(/[\s"']|,$/g, "");

    return idsString.split(",");
  } else if (idsString.includes("\n")) {
    return compact(idsString.split("\n"));
  }

  return idsString;
}

const actions = [
  adminConstants.GET_CUSTOM_REPORT_TYPES,
  adminConstants.CREATE_CUSTOM_REPORT,
];
const isLoading = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    reports: state.admin.complianceReports,
    isLoading: isLoading(state),
    error: errorSelector(state),
    customReportTypes: state.admin.customReportTypes,
    userId: state.user.id,
  };
};

const mapDispatchToProps = {
  getCustomReportTypes,
  createCustomReport,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = WithApolloClient<ConnectedProps<typeof connector>>;

interface State {
  initialFetching: boolean;
  selectedReportType: string;
}

class CustomReportForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const queryParams = queryString.parse(window.location.search);
    const isEmployer = has(queryParams, "employerId");
    const isUser = has(queryParams, "userId");
    let selectedReportType = "";
    if (isEmployer) {
      selectedReportType = "EmployerContributionReport";
    } else if (isUser) {
      selectedReportType = "IndividualContributionReport";
    }

    this.state = {
      initialFetching: true,
      selectedReportType,
    };
  }

  async componentDidMount() {
    await this.props.getCustomReportTypes(
      this.props.client as ApolloClient<object>
    );

    this.setState({ initialFetching: false });
  }

  formatReportValues = (values: FormValues) => {
    const selectedReportType = this.props.customReportTypes[
      this.state.selectedReportType
    ] as CustomReportType;
    const inputParametersByName = keyBy(selectedReportType.parameters, "name");

    return transform(
      values,
      (acc: Record<string, unknown>, val, key) => {
        const inputParam = inputParametersByName[key];
        // some fields are not in the input parameters, so we just pass them through
        if (!inputParam) {
          acc[key] = val;
          return acc;
        }

        // the UX input is a comma separated string, but the API expects an array
        if (inputParam.type === "Str" && inputParam.isArray) {
          acc[key] = cleanAndSplitIds(val as string);
          return acc;
        }

        acc[key] = val;
      },
      {}
    );
  };

  submitReport = (values: FormValues) => {
    const updatedValues = this.formatReportValues(values);
    const params = {
      requestedBy: this.props.userId,
      reportType: this.state.selectedReportType,
      ...updatedValues,
    };
    // params is a dynamically-built bag; cast to the action's input shape.
    void this.props
      .createCustomReport(
        this.props.client as ApolloClient<object>,
        params as Parameters<typeof createCustomReport>[1]
      )
      .then((result) => {
        if (result.success) {
          toast.success("Report created successfully");
          this.props.push("/dashboard/reports/custom");
        }
      });
  };

  _generateSchema = (parameters: ReportParameter[]) => {
    return yup.object({
      reportName: yup.string().label("Report Name").required(),
      ...transform(
        parameters,
        (acc: Record<string, yup.DateSchema | yup.StringSchema>, param) => {
          if (param.type === "Date") {
            acc[param.name] = yup.date().label(param.description);
            if (param.required) {
              acc[param.name] = (acc[param.name] as yup.DateSchema).required();
            }
          } else if (param.type === "Str") {
            acc[param.name] = yup.string().label(param.description);
            if (param.required) {
              acc[param.name] = (
                acc[param.name] as yup.StringSchema
              ).required();
            }
          }

          return acc;
        },
        {}
      ),
    });
  };

  _buildReportTypeForm = () => {
    const selectedReportType = this.props.customReportTypes[
      this.state.selectedReportType
    ] as CustomReportType;
    const intialValues = transform(
      selectedReportType.parameters,
      (acc: Record<string, unknown>, param) => {
        const queryParams = queryString.parse(window.location.search);
        const defaultValue = get(queryParams, param.name, undefined);
        acc[param.name] = defaultValue;
        return acc;
      },
      {}
    );

    const schema = this._generateSchema(selectedReportType.parameters);
    return (
      // Formik infers FormValues from initialValues (a dynamic param bag plus
      // reportName) — matching the canonical Beneficiaries idiom.
      <Formik
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => {
          this.submitReport(values);
        }}
        enableReinitialize={true}
        initialValues={{
          ...intialValues,
          reportName: "",
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
          setFieldValue,
        }) => (
          <Form noValidate onSubmit={handleSubmit} className="group-creation">
            <ScrollToFieldError />
            <Row className="mb-3">
              <Form.Group as={Col} md={6} key={"reportName"}>
                <Form.Label>Report Name (Required)</Form.Label>
                <Form.Control
                  name={"reportName"}
                  value={values.reportName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.reportName && !!errors.reportName}
                  isValid={touched.reportName && !errors.reportName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.reportName}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            {map(selectedReportType.parameters, (inputParams) => {
              let formInput: React.ReactNode = null;
              if (inputParams.type === "Date") {
                formInput = generateDateField({
                  handleBlur,
                  values,
                  touched,
                  errors,
                  setFieldValue,
                  inputParams,
                });
              } else if (inputParams.type === "Str") {
                formInput = generateStringInputField({
                  handleChange,
                  handleBlur,
                  values,
                  touched,
                  errors,
                  inputParams,
                });
              }

              return (
                <Row className="mb-3">
                  <Form.Group
                    as={Col}
                    md={6}
                    key={inputParams.name}
                    controlId={inputParams.name}
                  >
                    {formInput}
                  </Form.Group>
                </Row>
              );
            })}
            {this.props.error && <Alert type="error" msg={this.props.error} />}
            <div className="submit-row">
              <span className="cancel-btn">
                <Button
                  btnLabel="Cancel"
                  name="cancel"
                  color="cancel"
                  type="button"
                  onClick={() => this.props.push("/dashboard/reports/custom")}
                />
              </span>
              <Button
                btnLabel="Create Report"
                name="submit"
                withArrow={true}
                loading={this.props.isLoading}
              />
            </div>
          </Form>
        )}
      </Formik>
    );
  };

  render() {
    // first input is the report type, then we

    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    return (
      <div className="mega-container">
        <section className="page-title-wrap">
          <article className="text-cell">
            <h1 className="page-title">Custom Report Generator</h1>

            <div className="main-content">
              <Row className="mb-3">
                <Form.Group as={Col} md={6}>
                  <Form.Label>Select custom report type</Form.Label>
                  <Form.Select
                    name="reportType"
                    value={this.state.selectedReportType}
                    onChange={(e) => {
                      this.setState({ selectedReportType: e.target.value });
                    }}
                  >
                    <option value="" disabled>
                      Select Report Type
                    </option>
                    {map(this.props.customReportTypes, (item) => {
                      const reportType = item as CustomReportType;
                      return (
                        <option
                          value={reportType.reportType}
                          key={reportType.reportType}
                        >
                          {reportType.description}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              </Row>
              {this.state.selectedReportType && this._buildReportTypeForm()}
            </div>
          </article>
        </section>
      </div>
    );
  }
}

export default connector(withApollo<Props>(CustomReportForm));
